import { useMemo, useRef, useState } from "react";
import { Input } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";

import type { MentionableUser } from "@/services/notesService";
import {
  ACTIVE_MENTION,
  avatarColor,
  initials,
} from "@/views/shared/noteMentions";

type Props = {
  value: string;
  onChange: (value: string) => void;
  users: MentionableUser[];
  placeholder?: string;
  autoSize?: { minRows: number; maxRows: number };
  style?: React.CSSProperties;
  textAreaRef?: React.Ref<TextAreaRef>;
  /** Fires for Enter/Escape only when the mention menu is closed. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  /** The trial's member list is still being fetched. */
  usersLoading?: boolean;
  /** The member list could not be fetched — distinct from "nobody matched". */
  usersFailed?: boolean;
};

/**
 * How many people the menu holds at once.
 *
 * Generous rather than tight: with "@" alone the author is browsing the trial's
 * team, so the list should be long enough to scroll and recognise someone in,
 * not a six-row teaser. The container scrolls; typing narrows it.
 */
const MAX_SUGGESTIONS = 50;

/**
 * A textarea that offers trial members when the author types "@".
 *
 * Kept separate from NotesTab because both the compose box and the inline edit
 * box need it, and they are rendered in different parts of the tree.
 */
export default function MentionTextArea({
  value,
  onChange,
  users,
  placeholder,
  autoSize,
  style,
  textAreaRef,
  onKeyDown,
  disabled,
  usersLoading = false,
  usersFailed = false,
}: Props) {
  const [query, setQuery] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  const caretRef = useRef(0);

  const matches = useMemo(() => {
    if (query === null) return [];
    const needle = query.trim().toLowerCase();
    const pool = needle
      ? users.filter(
          (user) =>
            user.name.toLowerCase().includes(needle) ||
            user.email.toLowerCase().includes(needle),
        )
      : users;
    return pool.slice(0, MAX_SUGGESTIONS);
  }, [query, users]);

  // Open whenever the caret is in an "@…", even with nothing to show. Closing
  // silently on no matches reads as "nothing happened"; the empty state is what
  // tells the author the person cannot be tagged.
  const isOpen = query !== null;

  // Display names shared by two people on the trial. Shown with their email so
  // the author can tell them apart — and because an ambiguous name typed as
  // plain text resolves to nobody.
  const ambiguousNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const user of users) {
      if (user.name) counts.set(user.name, (counts.get(user.name) ?? 0) + 1);
    }
    return new Set(
      [...counts.entries()].filter(([, n]) => n > 1).map(([name]) => name),
    );
  }, [users]);

  const syncQuery = (text: string, caret: number) => {
    caretRef.current = caret;
    const match = ACTIVE_MENTION.exec(text.slice(0, caret));
    const next = match ? match[1] : null;
    setQuery((current) => {
      // A changed query means a different candidate list, so the highlight
      // starts over at the top rather than pointing into the old one.
      if (current !== next) setHighlighted(0);
      return next;
    });
  };

  const insertMention = (user: MentionableUser) => {
    const caret = caretRef.current;
    const before = value.slice(0, caret);
    const match = ACTIVE_MENTION.exec(before);
    if (!match) return;

    const start = caret - match[0].length;
    const next = `${value.slice(0, start)}@${user.name} ${value.slice(caret)}`;
    onChange(next);
    setQuery(null);

    const nextCaret = start + user.name.length + 2;
    requestAnimationFrame(() => {
      const node = document.activeElement as HTMLTextAreaElement | null;
      if (node && node.tagName === "TEXTAREA") {
        node.setSelectionRange(nextCaret, nextCaret);
        caretRef.current = nextCaret;
      }
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // With the menu open but empty, Enter must still submit and arrows must
    // still move the caret — an empty list has nothing to navigate.
    if (isOpen && matches.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlighted((i) => (i + 1) % matches.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlighted((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        insertMention(matches[highlighted]);
        return;
      }
      if (event.key === "Escape") {
        // Close the menu without letting Escape also cancel the edit.
        event.preventDefault();
        event.stopPropagation();
        setQuery(null);
        return;
      }
    } else if (isOpen && event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setQuery(null);
      return;
    }
    onKeyDown?.(event);
  };

  return (
    <div className="relative">
      {isOpen && (
        <div
          className="absolute bottom-full left-0 z-20 mb-1 w-[280px] max-h-[280px] overflow-y-auto rounded-lg border border-[#E5E7EB] bg-white shadow-lg py-1"
          role="listbox"
          aria-label="Tag a Vivo user on this trial"
        >
          {matches.length === 0 ? (
            <div className="px-3 py-2.5">
              <div className="text-[13px] font-medium text-[#4B5563]">
                {usersLoading
                  ? "Loading people…"
                  : usersFailed
                    ? "Could not load people"
                    : "No people match"}
              </div>
              <div className="text-[12px] text-[#9CA3AF] mt-0.5">
                {usersFailed
                  ? "Try again in a moment."
                  : "You can only tag Vivo users on this trial."}
              </div>
            </div>
          ) : (
            matches.map((user, index) => (
              <button
                key={user.id}
                type="button"
                role="option"
                aria-selected={index === highlighted}
                // onMouseDown, not onClick: the textarea must not lose focus
                // before the insert runs.
                onMouseDown={(event) => {
                  event.preventDefault();
                  insertMention(user);
                }}
                onMouseEnter={() => setHighlighted(index)}
                className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors ${
                  index === highlighted ? "bg-[#EBF7FC]" : "bg-white"
                }`}
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
                  style={{ background: avatarColor(user.name) }}
                >
                  {initials(user.name)}
                </span>
                <span className="flex flex-col min-w-0">
                  <span className="text-[13px] font-medium text-[#1F2933] truncate">
                    {user.name}
                  </span>
                  <span className="text-[12px] text-[#9CA3AF] truncate">
                    {ambiguousNames.has(user.name)
                      ? user.email
                      : user.role || user.email}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
      <Input.TextArea
        ref={textAreaRef}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoSize={autoSize}
        style={style}
        onChange={(event) => {
          onChange(event.target.value);
          syncQuery(event.target.value, event.target.selectionStart ?? 0);
        }}
        onKeyUp={(event) => {
          const node = event.target as HTMLTextAreaElement;
          syncQuery(node.value, node.selectionStart ?? 0);
        }}
        onClick={(event) => {
          const node = event.target as HTMLTextAreaElement;
          syncQuery(node.value, node.selectionStart ?? 0);
        }}
        onBlur={() => setQuery(null)}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}
